import React from "react";
import { Menu, MenuItem } from "../../../shared/components";
import { useTooltipContainer } from "../../../shared/providers/TooltipContainerProvider";
import { useAppSelector, useThunkDispatch } from "../../../store";
import { selectCycle } from "../../../store/profiling/actions";
import { cycleTree, type GroupSummary, type CycleTreeNode } from "../../../store/profiling/selectors";
import { sortCycleTree, sortGroups, type PassSort } from "../../../store/profiling/sorting";
import type { CycleEntry } from "../../../store/profiling/types";
import { ProfilingTooltip, TipRow, TipTitle, TipDivider } from "./ProfilingTooltip";
import { shortCycleId, formatMs, summarizeCycle, TRIGGER_LABELS } from "./viz";
import { Icon } from "../../../shared/components";

// A small custom tree (rows + chevrons). MUI's SimpleTreeView is a viable alternative now that the
// pop-out has its own Emotion cache, but this stays lightweight for the group/cycle rows we render.

const INDENT = 16;

// What a hovered row represents (drives the tooltip content).
type HoverTarget =
    | { kind: "group"; group: GroupSummary }
    | { kind: "cycle"; cycle: CycleEntry };

// The hovered target plus the pointer position (for the tooltip anchor).
type Hover = (HoverTarget & { x: number; y: number }) | null;

// --- Tooltip content ---------------------------------------------------------

// Both tooltips share one field vocabulary so a pass and its nested template renders read the same:
// "Parser time" (render + callback batches), "React commit" (react batch), each with its node count,
// plus the callback breakdown ("N× · total") — group rows are just the sums across their cycles.

const timeWithNodes = (ms: number, nodes: number) => `${formatMs(ms)} · ${nodes} node${nodes === 1 ? "" : "s"}`;

function GroupTooltipContent({ group }: { group: GroupSummary }) {
    return (
        <>
            <TipTitle>Render pass</TipTitle>
            <TipRow label="Root cycle" value={group.rootCycleId} />
            <TipRow label="Template" value={group.fileName ?? "(inline)"} />
            <TipDivider />
            <TipRow label="Wall time" value={formatMs(group.wallTime)} />
            <TipRow label="Parser time" value={timeWithNodes(group.parserSelfTime, group.parserSelfNodes)} />
            {group.reactCount > 0 && <TipRow label="React commit" value={timeWithNodes(group.reactSelfTime, group.reactSelfNodes)} />}
            <TipRow label={TRIGGER_LABELS.callback} value={group.callbackCount ? `${group.callbackCount}× · ${formatMs(group.callbackTime)}` : "none"} />
            <TipDivider />
            <TipRow label="Cycles" value={group.cycleCount} />
            <TipRow label="Templates" value={group.templateCount} />
            <TipRow label="Parser time (total)" value={timeWithNodes(group.parserTime, group.parserNodes)} />
            <TipRow label="React commit (total)" value={group.reactCount ? timeWithNodes(group.reactTime, group.reactNodes) : "off"} />
        </>
    );
}

function CycleTooltipContent({ cycle }: { cycle: CycleEntry }) {
    const s = summarizeCycle(cycle);
    return (
        <>
            <TipTitle>Template render</TipTitle>
            <TipRow label="Cycle" value={cycle.cycleId} />
            <TipRow label="Template" value={cycle.fileName ?? "(inline)"} />
            <TipDivider />
            <TipRow label="Wall time" value={formatMs(s.wallTime)} />
            <TipRow label="Parser time" value={timeWithNodes(s.renderTotal + s.callbackTotal, s.renderNodes + s.callbackNodes)} />
            <TipRow label={TRIGGER_LABELS.react} value={cycle.react ? timeWithNodes(s.reactTotal, s.reactNodes) : "off"} />
            <TipRow label={TRIGGER_LABELS.callback} value={s.callbackCount ? `${s.callbackCount}× · ${formatMs(s.callbackTotal)}` : "none"} />
        </>
    );
}

// --- Row bits ----------------------------------------------------------------

function Badges({ callbackCount, hasReact, templateCount }: { callbackCount: number; hasReact: boolean; templateCount?: number }) {
    return (
        <span className="cycle-badges">
            {templateCount != null && templateCount > 0 && <span className="cycle-badge tpl">{templateCount} ↯</span>}
            {callbackCount > 0 && <span className="cycle-badge cb">{callbackCount} cb</span>}
            {hasReact && <span className="cycle-badge react">react</span>}
        </span>
    );
}

// Shared two-line row layout for group AND cycle rows: render start time + wall time on top,
// template path + badges (pushed to the right edge) below.
function RowLabel({ id, time, fileName, badges }: { id: string; time: string; fileName?: string; badges: React.ReactNode }) {
    return (
        <div className="cycle-tree-label">
            <div className="label-line primary">
                <span className="cycle-id">{id}</span>
                <span className="cycle-time">{time}</span>
            </div>
            <div className="label-line secondary">
                <span className="cycle-file" style={{ direction: "rtl", textAlign: "left" }}>{fileName ?? "(inline)"}</span>
                {badges}
            </div>
        </div>
    );
}

function Chevron({ hasChildren, expanded, onToggle }: { hasChildren: boolean; expanded: boolean; onToggle: (e: React.MouseEvent) => void }) {
    return (
        <span
            className="cycle-chevron"
            title={hasChildren ? (expanded ? "Collapse" : "Expand") : undefined}
            onClick={hasChildren ? onToggle : undefined}
            style={{ visibility: hasChildren ? "visible" : "hidden" }}
        >
            {expanded ? <Icon>arrow_drop_down</Icon> : <Icon>arrow_right</Icon>}
        </span>
    );
}

// --- Component ---------------------------------------------------------------

type Props = {
    groups: GroupSummary[];
    /** Cycles matching the template/node-source filter values — rendered with an accent bar. */
    highlightedCycleIds?: Set<string> | null;
    /** Display order for the passes and (by subtree totals) their nested cycles. */
    sort: PassSort;
    /** Right-click "Select for comparison" — assigns the row's cycle to a Compare-view side. */
    onSelectForComparison?: (side: "a" | "b", cycleId: string) => void;
};

// The (already filtered) passes to list come from the parent; the profiling slice is still selected
// here to rebuild each pass's cycle tree.
function CycleTree({ groups: visibleGroups, highlightedCycleIds, sort, onSelectForComparison }: Props) {
    const dispatch = useThunkDispatch();
    const enabled = useAppSelector(s => s.profiling.enabled);
    const selectedCycleId = useAppSelector(s => s.profiling.selectedCycleId);
    const hasAnyGroups = useAppSelector(s => s.profiling.groupOrder.length > 0);
    const profiling = useAppSelector(s => s.profiling);

    const groups = React.useMemo(
        () => sortGroups(visibleGroups, sort).map(group => {
            const tree = cycleTree(profiling, group);
            return { group, tree: tree && sortCycleTree(tree, sort) };
        }),
        [visibleGroups, profiling, sort]
    );

    const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
    const [hover, setHover] = React.useState<Hover>(null);
    const [contextMenu, setContextMenu] = React.useState<{ x: number; y: number; cycleId: string } | null>(null);
    // The context Menu portals — anchor it to the pop-out-aware container (same as PassSortBar's Select).
    const tooltipContainerRef = useTooltipContainer();

    const pickForComparison = (side: "a" | "b") => {
        if (contextMenu) onSelectForComparison?.(side, contextMenu.cycleId);
        setContextMenu(null);
    };

    // Auto-expand each newest pass as it arrives, preserving prior expansions.  Newest by start time,
    // not display position — the display order is user-sortable.
    const newestRootId = React.useMemo(
        () => visibleGroups.reduce<GroupSummary | null>((max, g) => (!max || g.startTime > max.startTime ? g : max), null)?.rootCycleId,
        [visibleGroups]
    );
    React.useEffect(() => {
        if (newestRootId) setExpanded(prev => (prev.has(newestRootId) ? prev : new Set(prev).add(newestRootId)));
    }, [newestRootId]);

    const toggle = React.useCallback((id: string) => {
        setExpanded(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    if (groups.length === 0) {
        return (
            <div className="cycle-list-empty">
                {hasAnyGroups
                    ? "No passes match the current filters."
                    : enabled
                        ? "Interact with the canvas (e.g. change a parameter) to capture render passes."
                        : "Enable profiling to start capturing render passes."}
            </div>
        );
    }

    const renderRow = (opts: {
        cycleId: string;
        depth: number;
        hasChildren: boolean;
        isGroup: boolean;
        label: React.ReactNode;
        hover: HoverTarget;
    }) => {
        const { cycleId, depth, hasChildren, isGroup, label } = opts;
        const isExpanded = expanded.has(cycleId);
        const isFilterMatch = highlightedCycleIds?.has(cycleId) ?? false;
        return (
            <div
                className={`cycle-node-row${cycleId === selectedCycleId ? " selected" : ""}${isGroup ? " group" : ""}${isFilterMatch ? " filter-match" : ""}`}
                style={{ paddingLeft: 6 + depth * INDENT }}
                onClick={() => dispatch(selectCycle(cycleId))}
                onContextMenu={onSelectForComparison && (e => {
                    e.preventDefault();
                    setHover(null);
                    setContextMenu({ x: e.clientX, y: e.clientY, cycleId });
                })}
                onMouseMove={e => setHover({ ...opts.hover, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setHover(null)}
            >
                <Chevron hasChildren={hasChildren} expanded={isExpanded} onToggle={e => { e.stopPropagation(); toggle(cycleId); }} />
                {label}
            </div>
        );
    };

    const renderCycleNode = (node: CycleTreeNode, depth: number): React.ReactNode => {
        const { cycle } = node;
        const hasChildren = node.children.length > 0;
        return (
            <React.Fragment key={cycle.cycleId}>
                {renderRow({
                    cycleId: cycle.cycleId,
                    depth,
                    hasChildren,
                    isGroup: false,
                    hover: { kind: "cycle", cycle } as HoverTarget,
                    label: (
                        // cycleIds carry the ISO start-timestamp prefix, so shortCycleId is the render
                        // start time; the wall time matches what the tooltip leads with.
                        <RowLabel
                            id={shortCycleId(cycle.cycleId)}
                            time={formatMs(summarizeCycle(cycle).wallTime)}
                            fileName={cycle.fileName}
                            badges={<Badges callbackCount={cycle.callbacks.length} hasReact={!!cycle.react} />}
                        />
                    ),
                })}
                {hasChildren && expanded.has(cycle.cycleId) && node.children.map(child => renderCycleNode(child, depth + 1))}
            </React.Fragment>
        );
    };

    return (
        <div className="cycle-tree">
            {groups.map(({ group, tree }) => {
                const hasChildren = (tree?.children.length ?? 0) > 0;
                return (
                    <React.Fragment key={group.rootCycleId}>
                        {renderRow({
                            cycleId: group.rootCycleId,
                            depth: 0,
                            hasChildren,
                            isGroup: true,
                            hover: { kind: "group", group } as HoverTarget,
                            label: (
                                <RowLabel
                                    id={shortCycleId(group.rootCycleId)}
                                    time={formatMs(group.wallTime)}
                                    fileName={group.fileName}
                                    badges={<Badges callbackCount={group.callbackCount} hasReact={group.reactCount > 0} templateCount={group.templateCount} />}
                                />
                            ),
                        })}
                        {hasChildren && expanded.has(group.rootCycleId) && tree!.children.map(child => renderCycleNode(child, 1))}
                    </React.Fragment>
                );
            })}

            <ProfilingTooltip open={!!hover} x={hover?.x ?? 0} y={hover?.y ?? 0}>
                {hover && (hover.kind === "group"
                    ? <GroupTooltipContent group={hover.group} />
                    : <CycleTooltipContent cycle={hover.cycle} />)}
            </ProfilingTooltip>

            {onSelectForComparison && (
                <Menu
                    open={!!contextMenu}
                    onClose={() => setContextMenu(null)}
                    container={tooltipContainerRef.current}
                    anchorReference="anchorPosition"
                    anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
                >
                    <MenuItem dense onClick={() => pickForComparison("a")}>Select for comparison (Side A)</MenuItem>
                    <MenuItem dense onClick={() => pickForComparison("b")}>Select for comparison (Side B)</MenuItem>
                </Menu>
            )}
        </div>
    );
}

export default React.memo(CycleTree);
